import type { TFunction } from "i18next";
import {
  isArabicLanguage,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";
import {
  updatePaidMediaRule,
  createPaidMediaRule,
  updateSocialRule,
  createSocialRule,
  updateWebAnalyticsRule,
  createWebAnalyticsRule,
  updateSalesRule,
  createSalesRule,
  fetchWebAnalyticsCities,
  fetchSalesCities,
  type CitiesResponse,
} from "src/services/NotificationAnnouncementsAPIs";
import type {
  AnyRule,
  ConfigureFormState,
  DropdownOption,
  NotificationChannel,
  NotificationKind,
  NotificationOption,
  NotificationOptionsResponse,
  NotificationSection,
  NotificationSettingsItem,
  NotificationSystem,
  PageTarget,
  PaidMediaCampaign,
  PaidMediaRule,
  SalesProduct,
  SalesRule,
  SocialPlatformData,
  SocialRule,
  WebAnalyticsRule,
} from "../Types/NotificationTypes";
import {
  DEFAULT_FORM,
  NEGATIVE_THRESHOLD_ALLOWED_KEYS,
  OP_SYMBOL,
  PLATFORM_LABELS,
  UNIT_SYMBOLS,
} from "./NotificationConstants";

export const isNegativeThresholdAllowed = (system: NotificationSystem, key: string): boolean =>
  NEGATIVE_THRESHOLD_ALLOWED_KEYS[system]?.includes(key) ?? false;

export const localizeThresholdNumber = (
  value: number | null | undefined,
  language: string
): string => {
  const raw = String(value);
  return isArabicLanguage(language) ? wrapBidiIsolate(toArabicIndicDigitsInString(raw)) : raw;
};

export const thresholdText = (
  r: {
    operator: string | null;
    threshold_value: number | null;
    threshold_value_upper?: number | null;
  },
  sym: string,
  t: TFunction,
  language: string,
  unit = ""
): string => {
  const lower = `${localizeThresholdNumber(r.threshold_value, language)}${unit}`;
  if (r.operator === "between" && r.threshold_value_upper !== null) {
    const upper = `${localizeThresholdNumber(r.threshold_value_upper, language)}${unit}`;
    return t("notificationPreferences.table.thresholdBetween", {
      lower,
      upper,
      defaultValue: `between ${lower} and ${upper}`,
    });
  }
  return `${sym} ${lower}`;
};

const formatCityPart = (cities: string[]): string =>
  ` (${cities.slice(0, 2).join(", ")}${cities.length > 2 ? "…" : ""})`;

const getPaidMediaChipLabel = (
  item: NotificationSettingsItem,
  r: PaidMediaRule,
  sym: string,
  t: TFunction,
  language: string
): string => {
  const platStr = r.platforms.map(p => PLATFORM_LABELS[p] ?? p).join(", ");
  if (item.kind === "summary") {return `${platStr} · ${r.period}`;}
  return `${platStr} ${thresholdText(r, sym, t, language)}`;
};

const capitalizePlatform = (p: string): string =>
  p ? p.charAt(0).toUpperCase() + p.slice(1) : p;

const getSocialPlatformStr = (r: SocialRule): string => {
  const labelByPlatform = new Map(
    (r.targets ?? []).map(tgt => [tgt.platform, tgt.platform_label] as const)
  );
  return (r.platforms ?? [])
    .map(p => labelByPlatform.get(p) ?? capitalizePlatform(p))
    .join(", ");
};

const getSocialChipLabel = (
  item: NotificationSettingsItem,
  r: SocialRule,
  sym: string,
  t: TFunction,
  language: string
): string => {
  const platStr = getSocialPlatformStr(r);
  if (item.kind === "summary") {
    return platStr ? `${platStr} · ${r.period}` : r.period;
  }
  const unit = item.unit === "percentage" ? "%" : "";
  const cond = thresholdText(r, sym, t, language, unit);
  return platStr ? `${platStr} ${cond}` : cond;
};

const getWebAnalyticsChipLabel = (
  item: NotificationSettingsItem,
  r: WebAnalyticsRule,
  sym: string,
  t: TFunction,
  language: string
): string => {
  if (item.kind === "pages") {return r.period;}
  const unit = UNIT_SYMBOLS[r.unit] ?? "";
  const cityPart = r.cities?.length ? formatCityPart(r.cities) : "";
  return `${thresholdText(r, sym, t, language, unit)}${cityPart}`;
};

const formatScopePart = (r: SalesRule): string => {
  const names = [
    ...(r.cities ?? []),
    ...((r.products ?? []).map(p => p.product_name)),
  ];
  return names.length ? formatCityPart(names) : "";
};

const getSalesChipLabel = (
  item: NotificationSettingsItem,
  r: SalesRule,
  sym: string,
  t: TFunction,
  language: string
): string => {
  const unit = UNIT_SYMBOLS[r.unit] ?? "";
  const scope = formatScopePart(r);
  if (item.kind === "growth") {
    return `${thresholdText(r, sym, t, language, unit)} · ${r.comparison_period ?? ""}${scope}`;
  }
  return `${thresholdText(r, sym, t, language, unit)}${scope}`;
};

export const getRuleChipLabel = (
  item: NotificationSettingsItem,
  t: TFunction,
  language: string
): string => {
  const rule = item.rule;
  if (!rule) {return "";}
  const sym = OP_SYMBOL[rule.operator ?? ""] ?? "";

  if (item.system === "paid_media") {
    return getPaidMediaChipLabel(item, rule as PaidMediaRule, sym, t, language);
  }
  if (item.system === "social") {
    return getSocialChipLabel(item, rule as SocialRule, sym, t, language);
  }
  if (item.system === "sales") {
    return getSalesChipLabel(item, rule as SalesRule, sym, t, language);
  }
  return getWebAnalyticsChipLabel(item, rule as WebAnalyticsRule, sym, t, language);
};

type CitiesFetcher = (_search?: string, _lang?: string) => Promise<CitiesResponse>;

export const getCityFetcher = (system?: NotificationSystem): CitiesFetcher | null => {
  if (system === "web_analytics") {return fetchWebAnalyticsCities;}
  if (system === "sales") {return fetchSalesCities;}
  return null;
};

export const getPeriodOptionsForItem = (
  system: NotificationSystem,
  item: NotificationSettingsItem,
  options: NotificationOptionsResponse | null
): NotificationOption[] => {
  if (!options) {return [];}
  return options.metric_periods?.[system]?.[item.key] ?? [];
};

export const getComparisonOptionsForItem = (
  system: NotificationSystem,
  item: NotificationSettingsItem,
  options: NotificationOptionsResponse | null
): NotificationOption[] => {
  if (!options) {return [];}
  return options.metric_comparison_periods?.[system]?.[item.key] ?? [];
};

export const usesComparison = (item: NotificationSettingsItem): boolean => {
  const periods = item.allowed_periods ?? [];
  const comparisons = item.allowed_comparison_periods ?? [];
  return periods.length === 0 && comparisons.length > 0;
};

export const isEditingExistingRule = (item: NotificationSettingsItem): boolean =>
  item.configured && item.rule !== null;

export const hasConditionAndValueForKind = (kind: NotificationKind): boolean =>
  kind === "threshold" || kind === "city" || kind === "growth";

export const pageKey = (platform: string, pageId: string) => `${platform}::${pageId}`;

export const getPanelValidity = (
  section: NotificationSection,
  item: NotificationSettingsItem,
  form: ConfigureFormState
): boolean => {
  const showComparison = usesComparison(item);
  const hasConditionAndValue = hasConditionAndValueForKind(item.kind);
  const isBetween = form.operator === "between";

  const isPeriodValid = showComparison
    ? Boolean(form.comparison_period)
    : Boolean(form.period);
  const isConditionValid = !hasConditionAndValue || Boolean(form.operator);
  const allowNegativeThreshold = isNegativeThresholdAllowed(section.system, item.key);
  const isNonNegativeIfRequired = (value: string) =>
    allowNegativeThreshold || value.trim() === "" || Number.parseFloat(value) >= 0;

  const isValueValid =
    !hasConditionAndValue ||
    (form.threshold_value.trim() !== "" &&
      isNonNegativeIfRequired(form.threshold_value) &&
      (!isBetween ||
        (form.threshold_value_upper.trim() !== "" &&
          isNonNegativeIfRequired(form.threshold_value_upper) &&
          Number.parseFloat(form.threshold_value) !==
            Number.parseFloat(form.threshold_value_upper))));
  const isPlatformValid =
    section.system === "paid_media"
      ? form.platforms.length > 0
      : section.system !== "social" || form.social_platforms.length > 0;

  return isPeriodValid && isConditionValid && isValueValid && isPlatformValid;
};

export const getSaveButtonLabel = (
  saving: boolean,
  isEdit: boolean,
  t: TFunction
): string => {
  if (saving) {
    return t("notificationPreferences.configureThreshold.saving", {
      defaultValue: "Saving…",
    });
  }
  if (isEdit) {
    return t("notificationPreferences.configureThreshold.update", {
      defaultValue: "Update",
    });
  }
  return t("notificationPreferences.configureThreshold.save", {
    defaultValue: "Save Threshold",
  });
};

export const buildCampaignData = (
  selected: PaidMediaCampaign[],
  available: PaidMediaCampaign[]
): { map: Map<string, PaidMediaCampaign>; options: DropdownOption[] } => {
  const map = new Map<string, PaidMediaCampaign>();
  [...selected, ...available].forEach(c => {
    if (!map.has(c.campaign_id)) {map.set(c.campaign_id, c);}
  });
  const options = Array.from(map.values()).map(c => ({
    value: c.campaign_id,
    label: c.platform_label
      ? `${c.campaign_name} (${c.platform_label})`
      : c.campaign_name,
  }));
  return { map, options };
};

export const buildPageData = (
  socialPlatforms: SocialPlatformData[],
  targets: PageTarget[],
  selectedPlatforms: string[]
): { map: Map<string, PageTarget>; options: DropdownOption[] } => {
  const map = new Map<string, PageTarget>();
  socialPlatforms
    .filter(sp => sp.available)
    .forEach(sp =>
      sp.pages.forEach(page =>
        map.set(pageKey(page.platform, page.page_id), {
          platform: page.platform,
          page_id: page.page_id,
          platform_label: sp.label,
          page_name: page.page_name,
        })
      )
    );
  targets.forEach(tgt => {
    const k = pageKey(tgt.platform, tgt.page_id);
    if (!map.has(k)) {map.set(k, tgt);}
  });
  const options = Array.from(map.values())
    .filter(tgt => selectedPlatforms.includes(tgt.platform))
    .map(tgt => ({
      value: pageKey(tgt.platform, tgt.page_id),
      label: tgt.page_name
        ? `${tgt.page_name} - ${tgt.page_id} (${tgt.platform_label})`
        : `${tgt.page_id} (${tgt.platform_label})`,
    }));
  return { map, options };
};

export const buildCityOptions = (
  cities: string[],
  available: string[]
): DropdownOption[] =>
  Array.from(new Set([...cities, ...available])).map(c => ({
    value: c,
    label: c,
  }));

export const buildProductData = (
  selected: SalesProduct[],
  available: SalesProduct[]
): { map: Map<string, SalesProduct>; options: DropdownOption[] } => {
  const map = new Map<string, SalesProduct>();
  [...selected, ...available].forEach(p => {
    if (!map.has(p.product_id)) {map.set(p.product_id, p);}
  });
  const options = Array.from(map.values()).map(p => ({
    value: p.product_id,
    label: p.product_name,
  }));
  return { map, options };
};

export const buildPaidMediaForm = (r: PaidMediaRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  platforms: r.platforms ?? [],
  operator: r.operator ?? "gt",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "daily",
  campaigns: r.campaigns ?? [],
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

export const buildSocialForm = (r: SocialRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  operator: r.operator ?? "gt",
  threshold_type: r.threshold_type ?? "absolute",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "daily",
  social_platforms: Array.from(new Set((r.targets ?? []).map(tgt => tgt.platform))),
  targets: (r.targets ?? []).map(tgt => ({
    platform: tgt.platform,
    page_id: tgt.page_id,
    platform_label: tgt.platform_label,
    page_name: tgt.page_name,
  })),
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

export const buildWebAnalyticsForm = (r: WebAnalyticsRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  operator: r.operator ?? "gt",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "current_month",
  cities: r.cities ?? [],
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

export const buildSalesForm = (r: SalesRule): ConfigureFormState => ({
  ...DEFAULT_FORM,
  operator: r.operator ?? "gt",
  threshold_value: r.threshold_value === null ? "" : String(r.threshold_value),
  threshold_value_upper:
    r.threshold_value_upper === null ? "" : String(r.threshold_value_upper),
  period: r.period ?? "daily",
  comparison_period: r.comparison_period ?? "wow",
  cities: r.cities ?? [],
  products: r.products ?? [],
  email: r.email,
  push: r.push,
  in_app: r.in_app,
});

export const buildConfigureForm = (
  system: NotificationSystem,
  rule: AnyRule | null,
  defaultPeriod: string,
  defaultComparison: string
): ConfigureFormState => {
  if (!rule)
    {return {
      ...DEFAULT_FORM,
      period: defaultPeriod,
      comparison_period: defaultComparison || DEFAULT_FORM.comparison_period,
    };}
  if (system === "paid_media") {return buildPaidMediaForm(rule as PaidMediaRule);}
  if (system === "social") {return buildSocialForm(rule as SocialRule);}
  if (system === "sales") {return buildSalesForm(rule as SalesRule);}
  return buildWebAnalyticsForm(rule as WebAnalyticsRule);
};

export const updateRuleChannel = (
  sections: NotificationSection[],
  sectionIdx: number,
  itemIdx: number,
  channel: NotificationChannel,
  checked: boolean
): NotificationSection[] =>
  sections.map((s, si) => {
    if (si !== sectionIdx) {return s;}
    return {
      ...s,
      items: s.items.map((it, ii) => {
        if (ii !== itemIdx || !it.rule) {return it;}
        return { ...it, rule: { ...it.rule, [channel]: checked } };
      }),
    };
  });

export const buildPaidMediaPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    platforms: form.platforms,
    period: form.period,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) {payload.metric = item.key;}
  if (item.kind === "threshold") {
    payload.operator = form.operator;
    payload.threshold_value = Number.parseFloat(form.threshold_value) || 0;
    payload.threshold_value_upper =
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null;
    payload.campaigns = form.campaigns;
  }
  return payload;
};

export const savePaidMediaRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildPaidMediaPayload(item, form, isEdit);
  if (isEdit) {
    await updatePaidMediaRule((item.rule as PaidMediaRule).rule_id, payload);
  } else {
    await createPaidMediaRule(payload);
  }
};

export const buildSocialPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    period: form.period,
    targets: form.targets,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) {payload.rule_type = item.key;}
  if (item.kind === "threshold") {
    payload.operator = form.operator;
    payload.threshold_value = Number.parseFloat(form.threshold_value) || 0;
    payload.threshold_value_upper =
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null;
    if (item.key === "follower_growth") {payload.threshold_type = form.threshold_type;}
  }
  return payload;
};

export const saveSocialRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildSocialPayload(item, form, isEdit);
  if (isEdit) {
    await updateSocialRule((item.rule as SocialRule).rule_id, payload);
  } else {
    await createSocialRule(payload);
  }
};

export const buildWebAnalyticsPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    period: form.period,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) {payload.metric = item.key;}
  if (item.kind === "city") {
    payload.operator = form.operator;
    payload.threshold_value = Number.parseFloat(form.threshold_value) || 0;
    payload.threshold_value_upper =
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null;
    payload.cities = form.cities;
  }
  return payload;
};

export const saveWebAnalyticsRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildWebAnalyticsPayload(item, form, isEdit);
  if (isEdit) {
    await updateWebAnalyticsRule((item.rule as WebAnalyticsRule).rule_id, payload);
  } else {
    await createWebAnalyticsRule(payload);
  }
};

export const buildSalesPayload = (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    operator: form.operator,
    threshold_value: Number.parseFloat(form.threshold_value) || 0,
    threshold_value_upper:
      form.operator === "between"
        ? Number.parseFloat(form.threshold_value_upper) || 0
        : null,
    cities: form.cities,
    products: form.products,
    email: form.email,
    push: form.push,
    in_app: form.in_app,
    is_enabled: true,
  };
  if (!isEdit) {payload.metric = item.key;}

  if (usesComparison(item)) {
    payload.comparison_period = form.comparison_period;
  } else {
    payload.period = form.period;
  }
  return payload;
};

export const saveSalesRule = async (
  item: NotificationSettingsItem,
  form: ConfigureFormState,
  isEdit: boolean
): Promise<void> => {
  const payload = buildSalesPayload(item, form, isEdit);
  if (isEdit) {
    await updateSalesRule((item.rule as SalesRule).rule_id, payload);
  } else {
    await createSalesRule(payload);
  }
};
